import { useState } from 'react'
import { Toggle, ToggleGroup } from '@w/ui/components/toggle-group'
import { Badge } from '@w/ui/components/badge'
import { Button } from '@w/ui/components/button'
import {
  Progress,
  ProgressIndicator,
  ProgressTrack,
} from '@w/ui/components/progress'
import { useTranslation } from '../i18n'
import {
  rankBadge,
  type LemmaProgress,
  type LemmaStatus,
} from './vocabulary-progress'

const PAGE_SIZE = 100

export function VocabularyProgressList(props: {
  progress: LemmaProgress[]
  onOpenWord: (word: string, status: LemmaStatus, pageId?: string) => void
}) {
  const { t } = useTranslation('page')
  const [segment, setSegment] = useState('dictionary')
  const segments = [
    { value: 'dictionary', label: t('vocabDictionary'), count: props.progress.filter(row => !row.adopted).length },
    { value: 'adopted', label: t('vocabAdopted'), count: props.progress.filter(row => row.adopted && row.status !== 'learnt').length },
    { value: 'learned', label: t('vocabLearned'), count: props.progress.filter(row => row.status === 'learnt').length },
  ]
  const rows = props.progress.filter(row => segment === 'dictionary' ? !row.adopted
    : segment === 'learned' ? row.status === 'learnt' : Boolean(row.adopted) && row.status !== 'learnt')
  return (
    <section className="flex min-h-0 flex-col gap-4">
      <div className="flex flex-wrap items-center gap-4">
        <ToggleGroup className="w-full rounded-full *:min-w-0 *:grow *:first:rounded-s-full *:last:rounded-e-full" aria-label="Vocabulary filter" variant="outline" value={[segment]} onValueChange={values => { if (values[0]) setSegment(values[0]) }}>
          {segments.map(({ value, label, count }) => (
            <Toggle className="min-h-11 flex-auto flex-wrap gap-x-1 gap-y-0 px-2 text-xs sm:px-3 sm:text-sm" key={value} value={value} aria-label={`${label} ${count}`} data-testid={`vocab-filter-${value}`}>
              {label}
              <span data-testid={`vocab-${value}-count`} className="text-on-surface-secondary tabular-nums element">{new Intl.NumberFormat().format(count)}</span>
            </Toggle>
          ))}
        </ToggleGroup>
      </div>
      <WordGrid key={segment} onOpenWord={props.onOpenWord} rows={rows} />
    </section>
  )
}

function WordGrid(props: {
  rows: LemmaProgress[]
  onOpenWord: (word: string, status: LemmaStatus, pageId?: string) => void
}) {
  const { t } = useTranslation('page')
  const [limit, setLimit] = useState(PAGE_SIZE)
  const visibleRows = props.rows.slice(0, limit)

  if (props.rows.length === 0) {
    return (
      <p className="text-on-surface-secondary py-3 text-sm">
        {t('vocabEmptyTab')}
      </p>
    )
  }

  return (
    <div
      className="flex flex-wrap content-start gap-2"
      data-testid="vocabulary-progress-viewport"
    >
      {visibleRows.map(row => (
          <Button
            key={row.word}
            // className="border-separator bg-surface-2 flex h-auto max-w-full justify-start gap-3 rounded-xl border px-3 py-2 text-left"
            className="px-3 border border-separator"
            data-testid={`vocab-row-${row.word}`}
            variant="text"
            onClick={() => {
              props.onOpenWord(row.word, row.status, row.adopted?.pageIds?.toSorted()[0])
            }}
          >
            <span className="flex min-w-0 items-center gap-2" data-testid={`vocab-content-${row.word}`}>
            {row.adopted && row.status !== 'learnt' ? <span className="flex items-center gap-2" data-testid={`vocab-coverage-${row.word}`}>
              {row.definitionCount > 0 ? <>
                <Progress
                  aria-label={`${row.senseCount}/${row.definitionCount}`}
                  data-testid={`vocab-progress-${row.word}`}
                  max={row.definitionCount}
                  size="sm"
                  value={row.senseCount}
                  variant="circular"
                >
                  <ProgressTrack><ProgressIndicator /></ProgressTrack>
                </Progress>
                <span className="text-on-surface-secondary text-xs tabular-nums">{row.senseCount}/{row.definitionCount}</span>
              </> : <span className="text-on-surface-secondary text-xs tabular-nums element">{row.senseCount}</span>}
            </span> : null}
            <span className="text-on-surface truncate max-w-full font-medium">{row.word}</span>
            {row.rank > 0 ? <Badge size="xs" semanticRole="secondary">{rankBadge(row.rank)}</Badge> : null}
            </span>
          </Button>
      ))}
      {props.rows.length > limit ? <Button className="basis-full" variant="text" data-testid="vocab-show-more" onClick={() => { setLimit(limit + PAGE_SIZE) }}>{t('vocabShowMore')}</Button> : null}
    </div>
  )
}
